import React, { useState, useEffect } from 'react';
import { Thermometer, Droplets, RefreshCw, Cloud, Gauge } from 'lucide-react';

const App = () => {
  const [weatherData, setWeatherData] = useState({
    temperature_c: 25.0,
    humidity_percent: 67,
    pressure_pa: 101325
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Fetch data from your sensor API via nginx proxy
  const fetchWeatherData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/sensor2', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        cache: 'no-cache'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && typeof data.temperature_c !== 'undefined' && typeof data.humidity_percent !== 'undefined' && typeof data.pressure_pa !== 'undefined') {
        setWeatherData(data);
        setLastUpdated(new Date());
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWeatherData();
  }, []);

  const getTemperatureColor = (temp) => {
    if (temp < 10) return 'text-primary';
    if (temp < 20) return 'text-info';
    if (temp < 30) return 'text-success';
    if (temp < 35) return 'text-warning';
    return 'text-danger';
  };

  const getHumidityColor = (humidity) => {
    if (humidity < 30) return 'text-warning';
    if (humidity < 70) return 'text-success';
    return 'text-info';
  };

  const getTemperatureDescription = (temp) => {
    if (temp < 10) return 'Cold';
    if (temp < 20) return 'Cool';
    if (temp < 30) return 'Comfortable';
    if (temp < 35) return 'Warm';
    return 'Hot';
  };

  const getHumidityDescription = (humidity) => {
    if (humidity < 30) return 'Dry';
    if (humidity < 70) return 'Comfortable';
    return 'Humid';
  };

  const calculateComfortIndex = (temp, humidity) => {
    // Humidex formula - better than heat index for comfort perception
    // Only applies when temperature is above 15°C and humidity creates "feels like" effect
    if (temp < 15) {
      // At lower temperatures, use wind chill-like approach (simplified)
      // Just return temperature as the main factor
      return temp;
    }
    
    // Humidex formula for moderate to high temperatures
    const dewpoint = (temp - ((100 - humidity) / 5));
    const humidex = temp + 0.5555 * (6.112 * Math.pow(10, (7.591 * dewpoint) / (240.7 + dewpoint)) - 10);
    
    return humidex;
  };

  const getComfortDescription = (comfortIndex) => {
    if (comfortIndex < 0) return 'Freezing';
    if (comfortIndex < 10) return 'Cold';
    if (comfortIndex < 15) return 'Cool';
    if (comfortIndex < 20) return 'Pleasant';
    if (comfortIndex < 25) return 'Comfortable';
    if (comfortIndex < 30) return 'Warm';
    if (comfortIndex < 35) return 'Hot';
    if (comfortIndex < 40) return 'Very Hot';
    return 'Extremely Hot';
  };

  return (
    <div className="min-vh-100 bg-gradient-primary d-flex align-items-center py-4">
      <style jsx>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .weather-card {
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);
        }
        .metric-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .metric-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }
        .temperature-display {
          font-size: 3.5rem;
          font-weight: 300;
        }
        .humidity-display {
          font-size: 2.5rem;
          font-weight: 300;
        }
        .fade-in {
          animation: fadeIn 0.5s ease-in;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .stat-box {
          min-height: 120px;
        }
      `}</style>
      
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            {/* Header */}
            <div className="text-center text-white mb-4 fade-in">
              <Cloud size={48} className="mb-3" />
              <h1 className="display-4 fw-light mb-2">Weather Station</h1>
              <p className="lead opacity-75">Real-time environmental monitoring</p>
            </div>

            {/* Main Weather Card */}
            <div className="weather-card rounded-4 p-4 mb-4 fade-in">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="h4 mb-0 text-dark">Current Conditions</h2>
                <button 
                  className="btn btn-outline-primary rounded-pill d-flex align-items-center gap-2"
                  onClick={fetchWeatherData}
                  disabled={isLoading}
                >
                  <RefreshCw size={16} className={isLoading ? 'spinner-border spinner-border-sm' : ''} />
                  {isLoading ? 'Updating...' : 'Refresh'}
                </button>
              </div>

              <div className="row g-4">
                {/* Temperature Card */}
                <div className="col-md-6">
                  <div className="metric-card bg-white rounded-3 p-4 h-100 border-0 shadow-sm">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className={`p-3 rounded-circle bg-light ${getTemperatureColor(weatherData.temperature_c)}`}>
                          <Thermometer size={24} />
                        </div>
                        <div>
                          <h3 className="h6 text-muted mb-1">Temperature</h3>
                          <span className={`badge ${getTemperatureColor(weatherData.temperature_c).replace('text-', 'bg-')} bg-opacity-10 ${getTemperatureColor(weatherData.temperature_c)}`}>
                            {getTemperatureDescription(weatherData.temperature_c)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`temperature-display ${getTemperatureColor(weatherData.temperature_c)} mb-2`}>
                      {weatherData.temperature_c}°C
                    </div>
                    <div className="text-muted small">
                      {(weatherData.temperature_c * 9/5 + 32).toFixed(1)}°F
                    </div>
                  </div>
                </div>

                {/* Humidity Card */}
                <div className="col-md-6">
                  <div className="metric-card bg-white rounded-3 p-4 h-100 border-0 shadow-sm">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className={`p-3 rounded-circle bg-light ${getHumidityColor(weatherData.humidity_percent)}`}>
                          <Droplets size={24} />
                        </div>
                        <div>
                          <h3 className="h6 text-muted mb-1">Humidity</h3>
                          <span className={`badge ${getHumidityColor(weatherData.humidity_percent).replace('text-', 'bg-')} bg-opacity-10 ${getHumidityColor(weatherData.humidity_percent)}`}>
                            {getHumidityDescription(weatherData.humidity_percent)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`humidity-display ${getHumidityColor(weatherData.humidity_percent)} mb-2`}>
                      {weatherData.humidity_percent}%
                    </div>
                    <div className="progress" style={{height: '8px'}}>
                      <div 
                        className={`progress-bar ${getHumidityColor(weatherData.humidity_percent).replace('text-', 'bg-')}`}
                        style={{width: `${weatherData.humidity_percent}%`}}
                      />
                    </div>
                  </div>
                </div>

                {/* Pressure Card */}
                <div className="col-md-12">
                  <div className="metric-card bg-white rounded-3 p-4 h-100 border-0 shadow-sm">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="p-3 rounded-circle bg-light text-primary">
                          <Gauge size={24} />
                        </div>
                        <div>
                          <h3 className="h6 text-muted mb-1">Atmospheric Pressure</h3>
                        </div>
                      </div>
                    </div>
                    <div className="text-primary mb-2" style={{fontSize: '2rem', fontWeight: 300}}>
                      {weatherData.pressure_pa.toFixed(2)} hPa
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Info */}
              <div className="mt-4 pt-3 border-top">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <small className="text-muted">
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </small>
                  </div>
                  <div className="col-md-6 text-md-end">
                    <small className="text-muted">
                      Data from api.sergi.es/sensor2
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="row g-3 fade-in">
              <div className="col-md-4">
                <div className="stat-box bg-white bg-opacity-90 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                  <div className="fw-bold text-dark">Comfort Level</div>
                  <div className="text-success">Good</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-box bg-white bg-opacity-90 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                  <div className="fw-bold text-dark">Comfort Index</div>
                  <div className="text-warning">{calculateComfortIndex(weatherData.temperature_c, weatherData.humidity_percent).toFixed(1)}°C</div>
                  <div className="small text-muted">{getComfortDescription(calculateComfortIndex(weatherData.temperature_c, weatherData.humidity_percent))}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-box bg-white bg-opacity-90 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                  <div className="fw-bold text-dark">Status</div>
                  <div className="text-success">Online</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
